import React, { useState, useEffect } from 'react';
import { X, DollarSign, CreditCard, Building2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../../utils/formatters';
import { createPayment, getDistributorUnpaidOrders } from '../../api/wholesale/payments';
import { getDistributors } from '../../api/wholesale/distributors';
import { getBankChannels } from '../../api/bankChannels';

const CreatePaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [bankChannels, setBankChannels] = useState([]);
  const [loadingDistributors, setLoadingDistributors] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [formData, setFormData] = useState({
    distributor_id: '',
    payment_amount: '',
    payment_method: 'bank_transfer',
    payment_date: new Date().toISOString().split('T')[0],
    payment_allocation: 'account_balance',
    allocated_orders: [],
    reference_number: '',
    bank_name: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchDistributors();
      fetchBankChannels();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.distributor_id && formData.payment_allocation === 'order_payment') {
      fetchUnpaidOrders(formData.distributor_id);
    } else {
      setUnpaidOrders([]);
    }
  }, [formData.distributor_id, formData.payment_allocation]);

  const fetchDistributors = async () => {
    setLoadingDistributors(true);
    try {
      const response = await getDistributors();
      const data = response?.data || response || [];
      setDistributors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching distributors:', error);
      toast.error('Failed to load distributors');
    } finally {
      setLoadingDistributors(false);
    }
  };

  const fetchUnpaidOrders = async (distributorId) => {
    setLoadingOrders(true);
    try {
      const response = await getDistributorUnpaidOrders(distributorId);
      const data = response?.data || response || [];
      setUnpaidOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching unpaid orders:', error);
      toast.error('Failed to load unpaid orders. Please try again.');
      setUnpaidOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchBankChannels = async () => {
    try {
      const response = await getBankChannels(false, 'wholesale');
      const data = response?.data || response || [];
      setBankChannels(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching bank channels:', error);
      setBankChannels([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrderSelect = (orderId) => {
    setFormData((prev) => ({
      ...prev,
      allocated_orders: [orderId] // Single order only
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.distributor_id) {
      toast.error('Please select a distributor');
      return;
    }

    if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (formData.payment_allocation === 'order_payment' && formData.allocated_orders.length === 0) {
      toast.error('Please select an order to apply this payment to');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        distributor_id: parseInt(formData.distributor_id),
        payment_amount: parseFloat(formData.payment_amount),
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        payment_allocation: formData.payment_allocation,
        allocated_orders:
          formData.payment_allocation === 'order_payment' ? formData.allocated_orders : null,
        reference_number: formData.reference_number || null,
        bank_name: formData.bank_name || null,
        notes: formData.notes || null
      };

      const result = await createPayment(payload);

      // Show allocation details from API response
      const allocationDetails = result?.allocation_details;
      if (allocationDetails?.message) {
        toast.success(allocationDetails.message, { autoClose: 10000 });
      } else {
        toast.success('Payment recorded successfully');
      }

      onSuccess?.();
      onClose();

      // Reset form
      setFormData({
        distributor_id: '',
        payment_amount: '',
        payment_method: 'bank_transfer',
        payment_date: formatDateToISO(new Date()),
        payment_allocation: 'account_balance',
        allocated_orders: [],
        reference_number: '',
        bank_name: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-gray-800 to-gray-900 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Record Payment</h2>
              <p className="text-sm text-gray-300">Record a payment from a distributor</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Distributor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Distributor *
            </label>
            {loadingDistributors ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading distributors...
              </div>
            ) : (
              <select
                name="distributor_id"
                value={formData.distributor_id}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="">Select a distributor</option>
                {distributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.business_name} ({d.distributor_number})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Amount and Method Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">₦</span>
                <input
                  type="number"
                  name="payment_amount"
                  value={formData.payment_amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method *
              </label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date *</label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          {/* Payment Allocation */}
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <CreditCard className="inline h-4 w-4 mr-1" />
              How to Apply This Payment
            </label>
            <div className="space-y-3">
              {/* Option 1: Add to Account Balance */}
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-green-400 transition-colors">
                <input
                  type="radio"
                  name="payment_allocation"
                  value="account_balance"
                  checked={formData.payment_allocation === 'account_balance'}
                  onChange={handleChange}
                  className="mt-1 text-green-600 focus:ring-green-500"
                />
                <div>
                  <div className="font-medium text-gray-900">Add to Account Balance</div>
                  <div className="text-sm text-gray-500">
                    Deposits directly to the customer's account balance for future use
                  </div>
                </div>
              </label>

              {/* Option 2: Payment for Outstanding Balance */}
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-green-400 transition-colors">
                <input
                  type="radio"
                  name="payment_allocation"
                  value="outstanding_payment"
                  checked={formData.payment_allocation === 'outstanding_payment'}
                  onChange={handleChange}
                  className="mt-1 text-green-600 focus:ring-green-500"
                />
                <div>
                  <div className="font-medium text-gray-900">Payment for Outstanding Balance</div>
                  <div className="text-sm text-gray-500">
                    Pays off outstanding credit balance. Excess amount will be added to account
                    balance
                  </div>
                </div>
              </label>

              {/* Option 3: Apply to Specific Order */}
              <label className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-green-400 transition-colors">
                <input
                  type="radio"
                  name="payment_allocation"
                  value="order_payment"
                  checked={formData.payment_allocation === 'order_payment'}
                  onChange={handleChange}
                  className="mt-1 text-green-600 focus:ring-green-500"
                />
                <div>
                  <div className="font-medium text-gray-900">Apply to Specific Order</div>
                  <div className="text-sm text-gray-500">
                    Link this payment to ONE order. Excess amount will be added to account balance.
                  </div>
                </div>
              </label>
            </div>

            {/* Order Selection */}
            {formData.payment_allocation === 'order_payment' && formData.distributor_id && (
              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Select ONE Order to Pay
                </div>
                {loadingOrders ? (
                  <div className="flex items-center gap-2 text-gray-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading orders...
                  </div>
                ) : unpaidOrders.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    No unpaid orders found for this customer.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {unpaidOrders.map((order) => {
                      const isSelected = formData.allocated_orders.includes(order.order_id);
                      const balanceDue = parseFloat(order.balance_due || 0);
                      const paymentAmt = parseFloat(formData.payment_amount || 0);
                      const excess =
                        isSelected && paymentAmt > balanceDue ? paymentAmt - balanceDue : 0;

                      return (
                        <label
                          key={order.order_id}
                          className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-green-50 border-green-400'
                              : 'bg-gray-50 border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="order_selection"
                              checked={isSelected}
                              onChange={() => handleOrderSelect(order.order_id)}
                              className="text-green-600 focus:ring-green-500"
                            />
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">{order.order_number}</span>
                              {order.payment_status === 'credit' && (
                                <span className="text-xs text-amber-600">Credit Order</span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-red-600">
                            {formatCurrency(order.balance_due)} due
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Overpayment Preview */}
                {formData.allocated_orders.length > 0 &&
                  (() => {
                    const selectedOrderData = unpaidOrders.find(
                      (o) => o.order_id === formData.allocated_orders[0]
                    );
                    if (!selectedOrderData) return null;
                    const balanceDue = parseFloat(selectedOrderData.balance_due || 0);
                    const paymentAmt = parseFloat(formData.payment_amount || 0);
                    const excess = paymentAmt > balanceDue ? paymentAmt - balanceDue : 0;
                    const amountApplied = Math.min(paymentAmt, balanceDue);

                    if (paymentAmt > 0) {
                      return (
                        <div
                          className={`mt-3 p-3 rounded-lg border ${
                            excess > 0
                              ? 'bg-amber-50 border-amber-200'
                              : 'bg-green-50 border-green-200'
                          }`}
                        >
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Applied to order:</span>
                              <span className="font-medium">₦{amountApplied.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Remaining outstanding:</span>
                              <span
                                className={`font-medium ${balanceDue - amountApplied <= 0 ? 'text-green-600' : 'text-red-600'}`}
                              >
                                ₦{Math.max(0, balanceDue - amountApplied).toLocaleString()}
                                {balanceDue - amountApplied <= 0 && ' ✓ Fully Paid'}
                              </span>
                            </div>
                            {excess > 0 && (
                              <div className="flex justify-between pt-1 border-t border-amber-200">
                                <span className="text-amber-700 font-medium">
                                  Excess → Account Balance:
                                </span>
                                <span className="font-bold text-amber-700">
                                  ₦{excess.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
              </div>
            )}
          </div>

          {/* Reference & Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference / Transaction ID
              </label>
              <input
                type="text"
                name="reference_number"
                value={formData.reference_number}
                onChange={handleChange}
                placeholder="e.g., TRX123456"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Channel</label>
              <select
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                disabled={formData.payment_method === 'cash'}
                className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  formData.payment_method === 'cash'
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : ''
                }`}
              >
                <option value="">
                  {formData.payment_method === 'cash'
                    ? 'Not applicable for cash'
                    : 'Select a bank channel'}
                </option>
                {bankChannels.map((channel) => (
                  <option key={channel.id} value={channel.channel_name}>
                    {channel.channel_name} {channel.bank_name ? `(${channel.bank_name})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Additional notes about this payment..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4" />
                  Record Payment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePaymentModal;
